import React, { useEffect, useState } from 'react';
import { usePreferences } from '../contexts/PreferencesContext';
import type { Theme, ViewMode } from '../contexts/PreferencesContext';
import { apiClient } from '../api/client';

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '700px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: {
    marginBottom: '1.5rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.1rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
  },
  label: { fontSize: '0.95rem', color: '#333', fontWeight: 500 },
  toggleGroup: { display: 'flex', gap: '0.5rem' },
  btnActive: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnInactive: {
    padding: '0.5rem 1rem',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  resetBtn: {
    marginTop: '1rem',
    padding: '0.6rem 1.2rem',
    backgroundColor: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
  },
  fieldGroup: {
    marginBottom: '1rem',
  },
  fieldLabel: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.25rem',
  },
  submitBtn: {
    padding: '0.6rem 1.2rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  message: {
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  success: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  error: {
    backgroundColor: '#fbe9e7',
    color: '#c62828',
  },
};

export function SettingsPage() {
  const { preferences, updatePreference, resetPreferences } = usePreferences();

  const [phone, setPhone] = useState('');
  const [phoneMessage, setPhoneMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isPhoneSaving, setIsPhoneSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  useEffect(() => {
    apiClient<{ phone?: string | null }>('/users/me').then((user) => {
      if (user?.phone) setPhone(user.phone);
    }).catch(() => {});
  }, []);

  const handleSavePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    setPhoneMessage(null);
    setIsPhoneSaving(true);
    try {
      await apiClient('/users/me/profile', {
        method: 'PATCH',
        body: { phone: phone || undefined },
      });
      setPhoneMessage({ type: 'success', text: 'Phone number saved successfully' });
    } catch (err: any) {
      const msg = err?.message || 'Failed to save phone number';
      setPhoneMessage({ type: 'error', text: msg });
    } finally {
      setIsPhoneSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setIsSubmitting(true);
    try {
      await apiClient('/users/me/password', {
        method: 'PATCH',
        body: { currentPassword, newPassword },
      });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err?.message || 'Current password is incorrect';
      setPasswordMessage({ type: 'error', text: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Settings</h1>

      {/* Theme Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Theme</h2>
        <div style={styles.row}>
          <span style={styles.label}>
            Color Mode {preferences.theme === 'dark' ? '🌙' : '☀️'}
          </span>
          <div style={styles.toggleGroup}>
            <button
              style={preferences.theme === 'light' ? styles.btnActive : styles.btnInactive}
              onClick={() => updatePreference('theme', 'light' as Theme)}
            >
              Light
            </button>
            <button
              style={preferences.theme === 'dark' ? styles.btnActive : styles.btnInactive}
              onClick={() => updatePreference('theme', 'dark' as Theme)}
            >
              Dark
            </button>
          </div>
        </div>
      </div>

      {/* View Mode Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Default View Mode</h2>
        <div style={styles.row}>
          <span style={styles.label}>List pages default to</span>
          <div style={styles.toggleGroup}>
            <button
              style={preferences.defaultViewMode === 'card' ? styles.btnActive : styles.btnInactive}
              onClick={() => updatePreference('defaultViewMode', 'card' as ViewMode)}
            >
              Card
            </button>
            <button
              style={preferences.defaultViewMode === 'table' ? styles.btnActive : styles.btnInactive}
              onClick={() => updatePreference('defaultViewMode', 'table' as ViewMode)}
            >
              Table
            </button>
          </div>
        </div>
      </div>

      {/* Phone Number Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Phone Number (for SMS alerts)</h2>
        {phoneMessage && (
          <div style={{ ...styles.message, ...(phoneMessage.type === 'success' ? styles.success : styles.error) }}>
            {phoneMessage.text}
          </div>
        )}
        <form onSubmit={handleSavePhone}>
          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>Phone Number</label>
            <input
              style={styles.input}
              type="tel"
              placeholder="+1234567890"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <span style={{ display: 'block', fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
              Used for emergency SMS notifications from the GX feed
            </span>
          </div>
          <button style={styles.submitBtn} type="submit" disabled={isPhoneSaving}>
            {isPhoneSaving ? 'Saving...' : 'Save Phone Number'}
          </button>
        </form>
      </div>

      {/* Change Password Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Change Password</h2>
        {passwordMessage && (
          <div style={{ ...styles.message, ...(passwordMessage.type === 'success' ? styles.success : styles.error) }}>
            {passwordMessage.text}
          </div>
        )}
        <form onSubmit={handleChangePassword}>
          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>Current Password</label>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...styles.input, paddingRight: '2.5rem' }}
                type={showCurrentPw ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
              />
              <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#666', padding: '4px' }} aria-label={showCurrentPw ? 'Hide password' : 'Show password'}>
                {showCurrentPw ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...styles.input, paddingRight: '2.5rem' }}
                type={showNewPw ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
              <button type="button" onClick={() => setShowNewPw(!showNewPw)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#666', padding: '4px' }} aria-label={showNewPw ? 'Hide password' : 'Show password'}>
                {showNewPw ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.fieldLabel}>Confirm New Password</label>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...styles.input, paddingRight: '2.5rem' }}
                type={showConfirmPw ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button type="button" onClick={() => setShowConfirmPw(!showConfirmPw)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#666', padding: '4px' }} aria-label={showConfirmPw ? 'Hide password' : 'Show password'}>
                {showConfirmPw ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          <button style={styles.submitBtn} type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Reset */}
      <button style={styles.resetBtn} onClick={resetPreferences}>
        Reset to Defaults
      </button>
    </div>
  );
}
